import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import DashboardLayout from "layouts/dashboard/DashboardLayout";
import {
  getAppSettings,
  type StampVendorItem,
  updateAppSettings,
} from "api/appSettings";

type NumericFieldKey =
  | "profFee"
  | "discussionProfFeeLegalAdvicePerApp"
  | "discussionProfFeeFilingPerApp"
  | "discussionProfFeeFollowUpPerApp"
  | "stampPaperRateUa"
  | "stampPaperRatePoA"
  | "stampPaperRateHearing"
  | "notaryRatePerStampUa"
  | "notaryRateHearing"
  | "replyToObjectionReplyPerApp"
  | "replyToObjectionTmMPerApp"
  | "hearingInvoiceAmountPerApp"
  | "tmMBeforeHearingAmountPerApp"
  | "tmMAfterHearingAmountPerApp"
  | "subsequentHearingAmountPerApp"
  | "counterGovtFeePerApp"
  | "counterProfFeePerApp"
  | "oppositionGovtFeePerApp"
  | "oppositionProfFeePerApp"
  | "oppositionTmMBeforeHearingAmountPerApp"
  | "evidenceProfFeePerApp"
  | "rejoinderProfFeePerApp"
  | "oppositionHearingProfFeePerApp"
  | "tmMBeforeOppositionHearingAmountPerApp"
  | "tmMAfterOppositionHearingAmountPerApp"
  | "subsequentOppositionHearingAmountPerApp"
  | "followUpFilingChargesPerApp"
  | "followUpProcessingChargesPerApp"
  | "assignmentDeedPrepPerApp"
  | "assignmentTmpOwnershipPerApp"
  | "assignmentTmpAttorneyPerApp"
  | "assignmentPoaPrepPerApp"
  | "assignmentNocPrepPerApp"
  | "assignmentAffidavitPrepPerApp"
  | "assignmentSofPrepPerApp"
  | "renewalProfFeePerApp"
  | "renewalGovtFeePerApp"
  | "legalCertProfFeePerApp"
  | "legalCertGovtFeePerApp"
  | "surveillanceProfFeePerApp";

interface SettingsFormState {
  govtFeeOptions: string;
  expeditedFeeMapRows: { govtFee: string; expeditedFee: string }[];
  paymentDetails: {
    gpayPhonepe: string;
    accountHolder: string;
    accountNo: string;
    bank: string;
    branch: string;
    ifscCode: string;
  };
  stampVendors: StampVendorItem[];
  numericFields: Record<NumericFieldKey, string>;
}

interface FeeFieldDef {
  key: NumericFieldKey;
  label: string;
}

type ModuleId =
  | "new-application"
  | "follow-up"
  | "opposition"
  | "assignment"
  | "renewal"
  | "legal-certificate"
  | "surveillance";

interface FeeGroup {
  /** Stable id used to inject custom (non-numeric) UI inside specific groups. */
  id: string;
  module: ModuleId;
  title: string;
  description?: string;
  fields: FeeFieldDef[];
}

const MODULE_HEADINGS: Record<ModuleId, { title: string; subtitle: string }> = {
  "new-application": {
    title: "New Application",
    subtitle:
      "Discussion, Filing, and Status (Examination Report, Hearing, Stage 4A — defending when someone opposes your mark).",
  },
  "follow-up": {
    title: "Follow-up",
    subtitle: "Charges for attorney change and processing updates.",
  },
  opposition: {
    title: "Opposition",
    subtitle:
      "When YOUR client files Opposition against another mark. Only the filing fees are unique here — procedural fees (evidence, rejoinder, hearing, TM-M) share the same rates as New Application Stage 4A above.",
  },
  assignment: {
    title: "Assignment",
    subtitle:
      "Per-application charges that make up the Assignment invoice — one rate per named line item (deed, TM-P ownership, TM-P attorney, PoA, NOC, affidavit, statement of facts).",
  },
  renewal: {
    title: "Renewal",
    subtitle: "Per-application charges for renewing a registered trademark.",
  },
  "legal-certificate": {
    title: "Legal Certificate",
    subtitle: "Per-application charges for issuing a Legal Certificate.",
  },
  surveillance: {
    title: "Surveillance",
    subtitle: "Per-application charges for Surveillance.",
  },
};

const FEE_GROUPS: FeeGroup[] = [
  // ─── New Application ──────────────────────────────────────────────────
  {
    id: "discussion",
    module: "new-application",
    title: "Discussion",
    description: "Initial advice, filing estimate, and processing follow-up.",
    fields: [
      { key: "profFee", label: "Professional Fee" },
      { key: "discussionProfFeeLegalAdvicePerApp", label: "Legal Advice Per App" },
      { key: "discussionProfFeeFilingPerApp", label: "Filing Per App" },
      { key: "discussionProfFeeFollowUpPerApp", label: "Follow-up Per App" },
    ],
  },
  {
    id: "filing",
    module: "new-application",
    title: "Filing",
    description: "Per-stamp rates (Step 6 / Step 10) and stamp vendor list (Step 5).",
    fields: [
      { key: "stampPaperRateUa", label: "Stamp Paper Rate (UA)" },
      { key: "stampPaperRatePoA", label: "Stamp Paper Rate (PoA)" },
      { key: "notaryRatePerStampUa", label: "Notary Rate Per Stamp (UA)" },
    ],
  },
  {
    id: "status-reply",
    module: "new-application",
    title: "Status — Reply to Objection",
    description: "Substep 3B(vi): per-application charges for filing Reply / TM-M.",
    fields: [
      { key: "replyToObjectionReplyPerApp", label: "Reply Per App" },
      { key: "replyToObjectionTmMPerApp", label: "TM-M Per App" },
    ],
  },
  {
    id: "status-hearing",
    module: "new-application",
    title: "Status — Hearing after Objection",
    description: "Substep 3D(v–xxx): hearing invoice, stamp/notary rates, TM-M amounts.",
    fields: [
      { key: "hearingInvoiceAmountPerApp", label: "Hearing Invoice Per App" },
      { key: "stampPaperRateHearing", label: "Stamp Paper Rate (Hearing)" },
      { key: "notaryRateHearing", label: "Notary Rate (Hearing)" },
      { key: "tmMBeforeHearingAmountPerApp", label: "TM-M Before Hearing Per App" },
      { key: "tmMAfterHearingAmountPerApp", label: "TM-M After Hearing Per App" },
      { key: "subsequentHearingAmountPerApp", label: "Subsequent Hearing Per App" },
    ],
  },
  {
    id: "status-4a-counter",
    module: "new-application",
    title: "Status — Stage 4A — Counter Statement",
    description:
      "Substep 4A(vi): when someone opposes your mark, you file a Counter Statement.",
    fields: [
      { key: "counterGovtFeePerApp", label: "Govt Fee Per App" },
      { key: "counterProfFeePerApp", label: "Professional Fee Per App" },
    ],
  },
  {
    id: "status-4a-evidence-rejoinder",
    module: "new-application",
    title: "Status — Stage 4A — Evidence & Rejoinder",
    description:
      "Substeps 4A(xviii) and 4A(xxv). Same rates also apply to the standalone Opposition module below.",
    fields: [
      { key: "evidenceProfFeePerApp", label: "Evidence Per App" },
      { key: "rejoinderProfFeePerApp", label: "Rejoinder Per App" },
    ],
  },
  {
    id: "status-4a-opposition-hearing",
    module: "new-application",
    title: "Status — Stage 4A — Opposition Hearing",
    description:
      "Substeps 4A(xxxiii–l): hearing charges and TM-M amounts. Same rates also apply to the standalone Opposition module below.",
    fields: [
      { key: "oppositionHearingProfFeePerApp", label: "Hearing Per App" },
      {
        key: "tmMBeforeOppositionHearingAmountPerApp",
        label: "TM-M Before Hearing Per App",
      },
      {
        key: "tmMAfterOppositionHearingAmountPerApp",
        label: "TM-M After Hearing Per App",
      },
      {
        key: "subsequentOppositionHearingAmountPerApp",
        label: "Subsequent Hearing Per App",
      },
    ],
  },
  // ─── Follow-up ────────────────────────────────────────────────────────
  {
    id: "follow-up",
    module: "follow-up",
    title: "Follow-up Charges",
    description: "Per-application charges for attorney change and processing updates.",
    fields: [
      { key: "followUpFilingChargesPerApp", label: "Filing Charges Per App" },
      { key: "followUpProcessingChargesPerApp", label: "Processing Charges Per App" },
    ],
  },
  // ─── Opposition (when YOUR client files Opposition) ───────────────────
  {
    id: "opposition-filing",
    module: "opposition",
    title: "Filing (Stage 1 Step 8)",
    description:
      "Govt Fee and Professional Charges for filing the Notice of Opposition. Unique to the Opposition module.",
    fields: [
      { key: "oppositionGovtFeePerApp", label: "Govt Fee Per App" },
      { key: "oppositionProfFeePerApp", label: "Professional Fee Per App" },
    ],
  },
  {
    id: "opposition-tm-m-before-hearing",
    module: "opposition",
    title: "Invoice for TM-M before Opposition Hearing (Stage 3)",
    description:
      "Professional Charges for filing the TM-M form including the Govt. Fee, per application. Unique to the Opposition module (independent of New Application Stage 4A).",
    fields: [
      {
        key: "oppositionTmMBeforeHearingAmountPerApp",
        label: "Prof. Charges for filing TM-M (incl. Govt Fee) Per App",
      },
    ],
  },
  // ─── Assignment ───────────────────────────────────────────────────────
  {
    id: "assignment-invoice",
    module: "assignment",
    title: "Assignment Invoice",
    description: "Per-application charges that make up the Assignment invoice.",
    fields: [
      { key: "assignmentDeedPrepPerApp", label: "Preparation of Assignment Deed (incl. Stamp Paper) Per App" },
      { key: "assignmentTmpOwnershipPerApp", label: "Filing of TM-P (change in ownership, incl. Govt Fee) Per App" },
      { key: "assignmentTmpAttorneyPerApp", label: "Filing of TM-P (change in attorney, incl. Govt Fee) Per App" },
      { key: "assignmentPoaPrepPerApp", label: "Preparation of Power of Attorneys Per App" },
      { key: "assignmentNocPrepPerApp", label: "Preparation of No Objection Certificate Per App" },
      { key: "assignmentAffidavitPrepPerApp", label: "Preparation of Affidavit (No Court Proceedings) Per App" },
      { key: "assignmentSofPrepPerApp", label: "Preparation of Statement of Facts Per App" },
    ],
  },
  // ─── Renewal ──────────────────────────────────────────────────────────
  {
    id: "renewal-invoice",
    module: "renewal",
    title: "Renewal Invoice",
    description: "Per-application charges that make up the Renewal invoice.",
    fields: [
      { key: "renewalProfFeePerApp", label: "Professional Fee Per App" },
      { key: "renewalGovtFeePerApp", label: "Govt Fee Per App" },
    ],
  },
  // ─── Legal Certificate ────────────────────────────────────────────────
  {
    id: "legal-cert-invoice",
    module: "legal-certificate",
    title: "Legal Certificate Invoice",
    description: "Per-application charges that make up the Legal Certificate invoice.",
    fields: [
      { key: "legalCertProfFeePerApp", label: "Professional Fee Per App" },
      { key: "legalCertGovtFeePerApp", label: "Govt Fee Per App" },
    ],
  },
  // ─── Surveillance ─────────────────────────────────────────────────────
  {
    id: "surveillance-invoice",
    module: "surveillance",
    title: "Surveillance Invoice",
    description: "Per-application charges that make up the Surveillance invoice.",
    fields: [
      { key: "surveillanceProfFeePerApp", label: "Professional Fee Per App" },
    ],
  },
];

const NUMERIC_FIELDS: FeeFieldDef[] = FEE_GROUPS.flatMap((g) => g.fields);

const NUMERIC_FIELD_DEFAULTS: Partial<Record<NumericFieldKey, string>> = {
  profFee: "6500",
  oppositionTmMBeforeHearingAmountPerApp: "900",
  legalCertProfFeePerApp: "2100",
  legalCertGovtFeePerApp: "900",
  surveillanceProfFeePerApp: "12000",
  assignmentDeedPrepPerApp: "6000",
  assignmentTmpOwnershipPerApp: "14000",
  assignmentTmpAttorneyPerApp: "2000",
  assignmentPoaPrepPerApp: "3000",
  assignmentNocPrepPerApp: "1000",
  assignmentAffidavitPrepPerApp: "1000",
  assignmentSofPrepPerApp: "1000",
};

const DEFAULT_GOVT_FEE_OPTIONS = "4500, 9000";
const DEFAULT_EXPEDITED_FEE_ROWS = [
  { govtFee: "4500", expeditedFee: "20000" },
  { govtFee: "9000", expeditedFee: "40000" },
];

const PAYMENT_DETAIL_LABELS: Record<string, string> = {
  gpayPhonepe: "GPay / PhonePe",
  accountHolder: "Account Holder",
  accountNo: "Account Number",
  bank: "Bank",
  branch: "Branch",
  ifscCode: "IFSC Code",
};

function emptyForm(): SettingsFormState {
  return {
    govtFeeOptions: DEFAULT_GOVT_FEE_OPTIONS,
    expeditedFeeMapRows: DEFAULT_EXPEDITED_FEE_ROWS,
    paymentDetails: {
      gpayPhonepe: "",
      accountHolder: "",
      accountNo: "",
      bank: "",
      branch: "",
      ifscCode: "",
    },
    stampVendors: [{ name: "", phone: "", isDefault: true }],
    numericFields: NUMERIC_FIELDS.reduce((acc, field) => {
      acc[field.key] = NUMERIC_FIELD_DEFAULTS[field.key] ?? "";
      return acc;
    }, {} as Record<NumericFieldKey, string>),
  };
}

export default function AppSettingsView(): JSX.Element {
  const [form, setForm] = useState<SettingsFormState>(emptyForm);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const data = await getAppSettings();

        const numericFields = NUMERIC_FIELDS.reduce((acc, field) => {
          const value = data[field.key];
          const hasDefault = field.key in NUMERIC_FIELD_DEFAULTS;
          const isEmpty = value === null || value === undefined || (hasDefault && value === 0);
          acc[field.key] = isEmpty ? (NUMERIC_FIELD_DEFAULTS[field.key] ?? "") : String(value);
          return acc;
        }, {} as Record<NumericFieldKey, string>);

        const expeditedRows = Object.entries(data.govtFeeToExpeditedFee ?? {});
        setForm({
          govtFeeOptions:
            data.govtFeeOptions && data.govtFeeOptions.length > 0
              ? data.govtFeeOptions.join(", ")
              : DEFAULT_GOVT_FEE_OPTIONS,
          expeditedFeeMapRows:
            expeditedRows.length > 0
              ? expeditedRows.map(([govtFee, expeditedFee]) => ({
                  govtFee,
                  expeditedFee: String(expeditedFee),
                }))
              : DEFAULT_EXPEDITED_FEE_ROWS,
          paymentDetails: {
            gpayPhonepe: data.paymentDetails?.gpayPhonepe ?? "",
            accountHolder: data.paymentDetails?.accountHolder ?? "",
            accountNo: data.paymentDetails?.accountNo ?? "",
            bank: data.paymentDetails?.bank ?? "",
            branch: data.paymentDetails?.branch ?? "",
            ifscCode: data.paymentDetails?.ifscCode ?? "",
          },
          stampVendors:
            data.stampVendors && data.stampVendors.length > 0
              ? data.stampVendors
              : [{ name: "", phone: "", isDefault: true }],
          numericFields,
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load app settings");
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, []);

  const parsedGovtFeeOptions = useMemo(() => {
    return form.govtFeeOptions
      .split(",")
      .map((item) => Number(item.trim()))
      .filter((n) => !Number.isNaN(n) && n >= 0);
  }, [form.govtFeeOptions]);

  const onSave = async () => {
    try {
      setSaving(true);
      setError("");

      const govtFeeToExpeditedFee: Record<number, number> = {};
      form.expeditedFeeMapRows.forEach((row) => {
        const govtFee = Number(row.govtFee.trim());
        const expeditedFee = Number(row.expeditedFee.trim());
        if (!Number.isNaN(govtFee) && !Number.isNaN(expeditedFee)) {
          govtFeeToExpeditedFee[govtFee] = expeditedFee;
        }
      });

      const numericPayload = NUMERIC_FIELDS.reduce((acc, field) => {
        const raw = form.numericFields[field.key].trim();
        if (raw !== "") {
          const parsed = Number(raw);
          if (!Number.isNaN(parsed)) {
            acc[field.key] = parsed;
          }
        }
        return acc;
      }, {} as Record<NumericFieldKey, number>);

      const stampVendors = form.stampVendors
        .filter((v) => v.name.trim() || v.phone.trim())
        .map((v) => ({
          name: v.name.trim(),
          phone: v.phone.trim(),
          isDefault: !!v.isDefault,
        }));

      await updateAppSettings({
        ...numericPayload,
        govtFeeOptions: parsedGovtFeeOptions,
        govtFeeToExpeditedFee,
        gpayPhonepe: form.paymentDetails.gpayPhonepe.trim(),
        accountHolder: form.paymentDetails.accountHolder.trim(),
        accountNo: form.paymentDetails.accountNo.trim(),
        bank: form.paymentDetails.bank.trim(),
        branch: form.paymentDetails.branch.trim(),
        ifscCode: form.paymentDetails.ifscCode.trim(),
        stampVendors,
      });

      toast.success("App settings saved successfully.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save app settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout headerTitle="App Settings">
      <main className="flex-1 overflow-y-auto p-3 sm:p-6">
        <div className="bg-white rounded-lg border border-[#EEEEEE] p-4 sm:p-6 mx-auto">
          <div className="mb-5">
            <h1 className="text-[20px] font-bold text-[#222]">App Settings</h1>
            <p className="text-[13px] text-[#6D6D6D] mt-1">
              Configure fees, payment details, and stamp vendors.
            </p>
          </div>

          {loading ? (
            <p className="text-sm text-[#6D6D6D]">Loading settings...</p>
          ) : (
            <div className="flex flex-col gap-6">
              {!!error && (
                <div className="text-[13px] text-[#FB2C36] bg-[#FFF1F2] border border-[#FFD5D8] rounded-md px-3 py-2">
                  {error}
                </div>
              )}
              <section className="border border-[#EEEEEE] rounded-lg p-4">
                <h2 className="text-[14px] font-semibold text-[#222] mb-3">Payment Details</h2>
                <p className="text-[12px] text-[#6D6D6D] -mt-2 mb-3">
                  Account-level payment info shown on every invoice.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(form.paymentDetails).map(([key, value]) => (
                    <div key={key}>
                      <label className="block text-[12px] text-[#222] mb-1">{PAYMENT_DETAIL_LABELS[key] ?? key}</label>
                      <input
                        type="text"
                        value={value}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            paymentDetails: {
                              ...prev.paymentDetails,
                              [key]: e.target.value,
                            },
                          }))
                        }
                        className="w-full h-10 px-2.5 text-[12px] border border-[#EEEEEE] rounded-md focus:outline-none focus:ring-1 focus:ring-[#222]"
                      />
                    </div>
                  ))}
                </div>
              </section>

              {FEE_GROUPS.map((group, idx) => {
                const prevModule = idx > 0 ? FEE_GROUPS[idx - 1].module : null;
                const showModuleHeading = group.module !== prevModule;
                const heading = MODULE_HEADINGS[group.module];
                return (
                  <div key={group.id} className="flex flex-col gap-4">
                    {showModuleHeading && (
                      <div className="border-t border-[#EEEEEE] pt-4 mt-2 first:border-t-0 first:pt-0 first:mt-0">
                        <h2 className="text-[16px] font-bold text-[#222] uppercase tracking-wide">
                          {heading.title}
                        </h2>
                        <p className="text-[12px] text-[#6D6D6D] mt-0.5">
                          {heading.subtitle}
                        </p>
                      </div>
                    )}

                    <section className="border border-[#EEEEEE] rounded-lg p-4">
                      <div className="mb-3">
                        <h3 className="text-[14px] font-semibold text-[#222]">
                          {group.title}
                        </h3>
                        {group.description && (
                          <p className="text-[12px] text-[#6D6D6D] mt-0.5">
                            {group.description}
                          </p>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {group.fields.map((field) => (
                          <div key={field.key}>
                            <label className="block text-[12px] text-[#222] mb-1">
                              {field.label}
                            </label>
                            <input
                              type="number"
                              min={0}
                              value={form.numericFields[field.key]}
                              onChange={(e) =>
                                setForm((prev) => ({
                                  ...prev,
                                  numericFields: {
                                    ...prev.numericFields,
                                    [field.key]: e.target.value,
                                  },
                                }))
                              }
                              className="w-full h-10 px-2.5 text-[12px] border border-[#EEEEEE] rounded-md focus:outline-none focus:ring-1 focus:ring-[#222]"
                            />
                          </div>
                        ))}
                      </div>

                      {group.id === "discussion" && (
                        <div className="mt-5 pt-4 border-t border-[#EEEEEE] flex flex-col gap-4">
                          <div>
                            <label className="block text-[12px] font-medium text-[#222] mb-1">
                              Govt Fee Options (comma separated)
                            </label>
                            <input
                              type="text"
                              value={form.govtFeeOptions}
                              onChange={(e) =>
                                setForm((prev) => ({
                                  ...prev,
                                  govtFeeOptions: e.target.value,
                                }))
                              }
                              className="w-full md:w-1/2 h-10 px-2.5 text-[12px] border border-[#EEEEEE] rounded-md focus:outline-none focus:ring-1 focus:ring-[#222]"
                              placeholder="4500, 9000"
                            />
                          </div>

                          <div className="flex flex-col gap-2">
                            <p className="text-[12px] font-medium text-[#222]">
                              Govt Fee → Expedited Fee Map
                            </p>
                            {form.expeditedFeeMapRows.map((row, rowIdx) => (
                              <div
                                key={rowIdx}
                                className="grid grid-cols-[1fr_1fr_auto] gap-2"
                              >
                                <input
                                  type="number"
                                  value={row.govtFee}
                                  onChange={(e) =>
                                    setForm((prev) => {
                                      const rows = [...prev.expeditedFeeMapRows];
                                      rows[rowIdx] = {
                                        ...rows[rowIdx],
                                        govtFee: e.target.value,
                                      };
                                      return { ...prev, expeditedFeeMapRows: rows };
                                    })
                                  }
                                  className="h-10 px-2.5 text-[12px] border border-[#EEEEEE] rounded-md focus:outline-none focus:ring-1 focus:ring-[#222]"
                                  placeholder="Govt Fee"
                                />
                                <input
                                  type="number"
                                  value={row.expeditedFee}
                                  onChange={(e) =>
                                    setForm((prev) => {
                                      const rows = [...prev.expeditedFeeMapRows];
                                      rows[rowIdx] = {
                                        ...rows[rowIdx],
                                        expeditedFee: e.target.value,
                                      };
                                      return { ...prev, expeditedFeeMapRows: rows };
                                    })
                                  }
                                  className="h-10 px-2.5 text-[12px] border border-[#EEEEEE] rounded-md focus:outline-none focus:ring-1 focus:ring-[#222]"
                                  placeholder="Expedited Fee"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    setForm((prev) => ({
                                      ...prev,
                                      expeditedFeeMapRows:
                                        prev.expeditedFeeMapRows.length === 1
                                          ? prev.expeditedFeeMapRows
                                          : prev.expeditedFeeMapRows.filter(
                                              (_, i) => i !== rowIdx,
                                            ),
                                    }))
                                  }
                                  className="h-10 px-3 text-[12px] border border-[#EEEEEE] rounded-md text-[#6D6D6D] hover:bg-[#F7F7F7]"
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() =>
                                setForm((prev) => ({
                                  ...prev,
                                  expeditedFeeMapRows: [
                                    ...prev.expeditedFeeMapRows,
                                    { govtFee: "", expeditedFee: "" },
                                  ],
                                }))
                              }
                              className="w-fit h-9 px-3 text-[12px] font-semibold text-[#222] border border-[#EEEEEE] rounded-md hover:bg-[#F7F7F7]"
                            >
                              + Add Fee Mapping
                            </button>
                          </div>
                        </div>
                      )}

                      {group.id === "filing" && (
                        <div className="mt-5 pt-4 border-t border-[#EEEEEE] flex flex-col gap-2">
                          <p className="text-[12px] font-medium text-[#222]">
                            Stamp Vendors (Filing Step 5)
                          </p>
                          {form.stampVendors.map((vendor, vIdx) => (
                            <div
                              key={vIdx}
                              className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-center"
                            >
                              <input
                                type="text"
                                placeholder="Vendor Name"
                                value={vendor.name}
                                onChange={(e) =>
                                  setForm((prev) => {
                                    const list = [...prev.stampVendors];
                                    list[vIdx] = { ...list[vIdx], name: e.target.value };
                                    return { ...prev, stampVendors: list };
                                  })
                                }
                                className="h-10 px-2.5 text-[12px] border border-[#EEEEEE] rounded-md focus:outline-none focus:ring-1 focus:ring-[#222]"
                              />
                              <input
                                type="text"
                                placeholder="Phone"
                                value={vendor.phone}
                                onChange={(e) =>
                                  setForm((prev) => {
                                    const list = [...prev.stampVendors];
                                    list[vIdx] = { ...list[vIdx], phone: e.target.value };
                                    return { ...prev, stampVendors: list };
                                  })
                                }
                                className="h-10 px-2.5 text-[12px] border border-[#EEEEEE] rounded-md focus:outline-none focus:ring-1 focus:ring-[#222]"
                              />
                              <label className="flex items-center gap-2 text-[12px] text-[#222]">
                                <input
                                  type="radio"
                                  checked={!!vendor.isDefault}
                                  onChange={() =>
                                    setForm((prev) => ({
                                      ...prev,
                                      stampVendors: prev.stampVendors.map((v, i) => ({
                                        ...v,
                                        isDefault: i === vIdx,
                                      })),
                                    }))
                                  }
                                />
                                Default
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  setForm((prev) => ({
                                    ...prev,
                                    stampVendors:
                                      prev.stampVendors.length === 1
                                        ? prev.stampVendors
                                        : prev.stampVendors.filter((_, i) => i !== vIdx),
                                  }))
                                }
                                className="h-10 px-3 text-[12px] border border-[#EEEEEE] rounded-md text-[#6D6D6D] hover:bg-[#F7F7F7]"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                stampVendors: [
                                  ...prev.stampVendors,
                                  { name: "", phone: "", isDefault: false },
                                ],
                              }))
                            }
                            className="w-fit h-9 px-3 text-[12px] font-semibold text-[#222] border border-[#EEEEEE] rounded-md hover:bg-[#F7F7F7]"
                          >
                            + Add Vendor
                          </button>
                        </div>
                      )}
                    </section>
                  </div>
                );
              })}

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => void onSave()}
                  disabled={saving}
                  className="h-10 px-4 text-[12px] font-semibold text-white rounded-md bg-[#222222] hover:bg-[#333333] transition-colors disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Settings"}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </DashboardLayout>
  );
}
